import type { CmmdCloudProjectLifecyclePreflight } from "@cmmd-forge/contracts";

export type CloudEnvironmentRecoveryStageId = "identity" | "wake" | "service" | "registration";

export type CloudEnvironmentRecovery =
  | {
      readonly outcome: "safe-success";
      readonly stages: readonly {
        readonly id: CloudEnvironmentRecoveryStageId;
        readonly state: "passed";
      }[];
    }
  | {
      readonly outcome: "refused";
      readonly stages: readonly {
        readonly id: CloudEnvironmentRecoveryStageId;
        readonly state: "passed" | "blocked";
      }[];
      readonly reason: string;
    };

const UNKNOWN_EFFECTS_REASON =
  "CMMD did not provide a safe wake target or the effects of an escalation.";

export function deriveCloudEnvironmentRecovery(
  receipt: CmmdCloudProjectLifecyclePreflight,
): CloudEnvironmentRecovery {
  const identity = { id: "identity", state: "passed" } as const;
  if (receipt.runtime === null) {
    return {
      outcome: "refused",
      stages: [identity, { id: "wake", state: "blocked" }],
      reason: UNKNOWN_EFFECTS_REASON,
    };
  }
  if (receipt.runtime.desiredState === "stopped") {
    return {
      outcome: "refused",
      stages: [identity, { id: "wake", state: "blocked" }],
      reason: "This Environment is paused. Resume it before recovery can wake it.",
    };
  }
  if (receipt.runtime.desiredState !== "running") {
    return {
      outcome: "refused",
      stages: [identity, { id: "wake", state: "blocked" }],
      reason: UNKNOWN_EFFECTS_REASON,
    };
  }
  if (receipt.runtime.status !== "running") {
    return {
      outcome: "refused",
      stages: [identity, { id: "wake", state: "blocked" }],
      reason: "CMMD has not confirmed that the Environment is awake.",
    };
  }
  if (receipt.runtime.supervisedService?.status !== "server_ready") {
    return {
      outcome: "refused",
      stages: [identity, { id: "wake", state: "passed" }, { id: "service", state: "blocked" }],
      reason: "CMMD has not confirmed Forge service readiness.",
    };
  }
  if (receipt.runtime.readinessPhase !== "server_ready") {
    return {
      outcome: "refused",
      stages: [identity, { id: "wake", state: "passed" }, { id: "service", state: "blocked" }],
      reason: "CMMD has not confirmed guest readiness.",
    };
  }
  if (receipt.registration === null) {
    return {
      outcome: "refused",
      stages: [
        identity,
        { id: "wake", state: "passed" },
        { id: "service", state: "passed" },
        { id: "registration", state: "blocked" },
      ],
      reason: "CMMD has not confirmed registration after recovery.",
    };
  }
  return {
    outcome: "safe-success",
    stages: [
      identity,
      { id: "wake", state: "passed" },
      { id: "service", state: "passed" },
      { id: "registration", state: "passed" },
    ],
  };
}

export function validateEnvironmentRecoveryReceipts(
  receipts: readonly CmmdCloudProjectLifecyclePreflight[],
  runtimeId: string | null,
): CmmdCloudProjectLifecyclePreflight | null {
  if (runtimeId === null || receipts.length === 0) return null;
  const first = receipts[0];
  if (first === undefined || first.runtime?.runtimeId !== runtimeId) return null;
  return receipts.every(
    (receipt) =>
      receipt.runtime?.runtimeId === runtimeId &&
      receipt.runtime.credentialEpoch === first.runtime?.credentialEpoch,
  )
    ? first
    : null;
}
